import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Badge } from "@/shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { FileText, Plus, Loader2, Pencil, Trash2, Scale, FolderOpen, Eye, ExternalLink } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useToast } from "@/shared/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  DOCUMENT_TYPES,
  LEGAL_CATEGORIES,
  LEGAL_GROUP_ORDER,
  DOCUMENT_FOLDERS,
  FOLDER_ORDER,
  getDocumentTypeLabel,
  getLegalCategoryLabel,
  getLegalCategoryGroup,
  getFolderLabel,
} from "@/shared/constants/institutionDocuments";
import AiMarkdownRenderer from "@/shared/components/finance/AiMarkdownRenderer";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import DocumentPreview from "./DocumentPreview";

interface InstitutionDocument {
  id: string;
  institution_id: string;
  title: string;
  content: string;
  document_type: string;
  legal_category: string | null;
  folder: string | null;
  attachment_url: string | null;
  version: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Props {
  institutionId: string;
  institutionName: string;
}

const DocumentosInstituicao = ({ institutionId, institutionName }: Props) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [docs, setDocs] = useState<InstitutionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [documentType, setDocumentType] = useState("outro");
  const [legalCategory, setLegalCategory] = useState<string | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  const [version, setVersion] = useState("1.0");
  const [saving, setSaving] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<InstitutionDocument | null>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterFolder, setFilterFolder] = useState<string>("all");

  const canCreate = hasPermission("instituicoes", "incluir");
  const canEdit = hasPermission("instituicoes", "alterar");
  const canDelete = hasPermission("instituicoes", "excluir");

  const fetchDocs = () => {
    setLoading(true);
    supabase
      .from("institution_documents")
      .select("id, institution_id, title, content, document_type, legal_category, folder, attachment_url, version, is_active, created_at, updated_at")
      .eq("institution_id", institutionId)
      .eq("is_active", true)
      .or("arquivo_removido.is.null,arquivo_removido.eq.false")
      .order("updated_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          console.error("institution_documents fetch:", error);
          setDocs([]);
        } else {
          setDocs((data || []) as InstitutionDocument[]);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!institutionId) return;
    fetchDocs();
  }, [institutionId]);

  const openNew = () => {
    setEditingId(null);
    setTitle("");
    setContent("");
    setDocumentType("outro");
    setLegalCategory(null);
    setFolder(null);
    setVersion("1.0");
    setDialogOpen(true);
  };

  const openEdit = (doc: InstitutionDocument) => {
    setEditingId(doc.id);
    setTitle(doc.title);
    setContent(doc.content);
    setDocumentType(doc.document_type);
    setLegalCategory(doc.legal_category ?? null);
    setFolder(doc.folder ?? null);
    setVersion(doc.version);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }

    setSaving(true);
    const now = new Date().toISOString();
    const payload = {
      title: title.trim(),
      content: content,
      document_type: documentType,
      legal_category: legalCategory || null,
      folder: folder || null,
      version: version,
      institution_id: institutionId,
      created_by: user?.id,
      extraction_status: "concluido" as const,
      extraction_method: "manual",
      extracted_chars: content.length,
      extracted_at: now,
    };

    if (editingId) {
      const { error } = await supabase
        .from("institution_documents")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", editingId);
      if (error) {
        toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Documento atualizado." });
        fetchDocs();
        setDialogOpen(false);
      }
    } else {
      const { error } = await supabase.from("institution_documents").insert(payload);
      if (error) {
        toast({ title: "Erro ao cadastrar", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Documento cadastrado." });
        fetchDocs();
        setDialogOpen(false);
      }
    }
    setSaving(false);
  };

  const handleDelete = async (docId: string) => {
    if (!canDelete || !confirm("Excluir este documento da base?")) return;
    const { error } = await supabase
      .from("institution_documents")
      .update({ is_active: false })
      .eq("id", docId);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Documento removido." });
      fetchDocs();
    }
  };

  const openView = (doc: InstitutionDocument) => {
    setViewingDoc(doc);
    setViewDialogOpen(true);
  };

  const openAttachment = async (doc: InstitutionDocument) => {
    if (!doc.attachment_url) return;
    const { data, error } = await supabase.storage.from("institution-documents").createSignedUrl(doc.attachment_url, 3600);
    if (error) {
      toast({ title: "Erro ao abrir arquivo", description: error.message, variant: "destructive" });
      return;
    }
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const filteredDocs = useMemo(() => {
    return docs.filter((doc) => {
      if (filterType !== "all" && doc.document_type !== filterType) return false;
      if (filterCategory !== "all" && doc.legal_category !== filterCategory) return false;
      if (filterFolder !== "all" && doc.folder !== filterFolder) return false;
      return true;
    });
  }, [docs, filterType, filterCategory, filterFolder]);

  const docsByGroup = useMemo(() => {
    const hasFolder = filteredDocs.some((d) => d.folder);
    if (hasFolder) {
      const byFolder: Record<string, InstitutionDocument[]> = {};
      for (const doc of filteredDocs) {
        const key = doc.folder || "outro";
        if (!byFolder[key]) byFolder[key] = [];
        byFolder[key].push(doc);
      }
      const order = [...FOLDER_ORDER];
      const rest = Object.keys(byFolder).filter((k) => !order.includes(k));
      const raw = [...order, ...rest].filter((k) => byFolder[k]?.length).map((folder) => ({ folder, group: getFolderLabel(folder), items: byFolder[folder] }));

      return raw.flatMap(({ folder, group, items }) => {
        if (folder !== "legislacao_federal") return [{ group, items }];
        const leis = items.filter((d) => d.legal_category === "lei_federal" || d.legal_category === "lei_complementar" || d.legal_category === "decreto_federal" || !d.legal_category);
        const nrs = items.filter((d) => d.legal_category === "norma_regulamentadora");
        const outros = items.filter((d) => !leis.includes(d) && !nrs.includes(d));
        const result: { group: string; items: InstitutionDocument[] }[] = [];
        if (leis.length) result.push({ group: `${group} — Leis e Códigos`, items: leis });
        if (nrs.length) result.push({ group: `${group} — Normas Regulamentadoras (NRs)`, items: nrs });
        if (outros.length) result.push({ group: `${group} — Outros`, items: outros });
        return result.length ? result : [{ group, items }];
      });
    }
    const groups: Record<string, InstitutionDocument[]> = {};
    for (const doc of filteredDocs) {
      const group = getLegalCategoryGroup(doc.legal_category);
      if (!groups[group]) groups[group] = [];
      groups[group].push(doc);
    }
    const order = [...LEGAL_GROUP_ORDER];
    const rest = Object.keys(groups).filter((g) => !order.includes(g));
    return [...order, ...rest].filter((g) => groups[g]?.length).map((group) => ({ group, items: groups[group] }));
  }, [filteredDocs]);

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Base de documentos da instituição <strong>{institutionName}</strong>. A IA consulta apenas estes documentos. Inclua novos arrastando para a área de upload ou pelo botão abaixo.
              </p>
              {canCreate && (
              <div className="flex items-center gap-2">
                <Button onClick={openNew} size="sm" className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Novo documento
                </Button>
              </div>
            )}
            </div>
            {docs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[180px] h-8">
                    <SelectValue placeholder="Tipo de documento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {DOCUMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[200px] h-8">
                    <SelectValue placeholder="Categoria jurídica" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {LEGAL_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.group} — {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterFolder} onValueChange={setFilterFolder}>
                  <SelectTrigger className="w-[200px] h-8">
                    <SelectValue placeholder="Pasta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as pastas</SelectItem>
                    {DOCUMENT_FOLDERS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground border rounded-lg border-dashed">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>Nenhum documento na base ainda.</p>
              <p className="text-xs mt-1">Adicione editais, atas, convenções e normas para a IA responder consultas.</p>
              {canCreate && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="outline" onClick={openNew}>
                    Adicionar primeiro documento
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {docsByGroup.map(({ group, items }) => (
                <div key={group} className="space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground border-b pb-1.5">
                    <FolderOpen className="h-4 w-4 shrink-0" />
                    <span className="text-xs font-semibold uppercase tracking-wider">{group}</span>
                    <Badge variant="outline" className="text-[10px] font-normal">
                      {items.length} {items.length === 1 ? "documento" : "documentos"}
                    </Badge>
                  </div>
                  <div className="space-y-1.5 pl-1">
                    {items.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/40 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{doc.title}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <Badge variant="secondary" className="text-[10px]">
                              {getDocumentTypeLabel(doc.document_type)}
                            </Badge>
                            {doc.folder && (
                              <Badge variant="outline" className="text-[10px]">
                                {getFolderLabel(doc.folder)}
                              </Badge>
                            )}
                            {doc.legal_category && (
                              <Badge variant="outline" className="text-[10px] gap-0.5">
                                <Scale className="h-2.5 w-2.5" />
                                {getLegalCategoryLabel(doc.legal_category)}
                              </Badge>
                            )}
                            <span className="text-[10px] text-muted-foreground">v{doc.version}</span>
                            <span className="text-[10px] text-muted-foreground">
                              {format(new Date(doc.updated_at), "dd/MM/yyyy", { locale: ptBR })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {doc.attachment_url && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openAttachment(doc)}
                              title="Abrir arquivo (PDF/DOCX)"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openView(doc)}
                            title="Visualizar conteúdo (texto)"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <div className="flex flex-col gap-0.5">
                            {canEdit && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(doc)} title="Editar">
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleDelete(doc.id)}
                                title="Excluir"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar documento" : "Novo documento"}</DialogTitle>
            <DialogDescription className="sr-only">Formulário de cadastro ou edição de documento institucional</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Ata da Assembleia 01/2025" />
              </div>
              <div className="space-y-2">
                <Label>Tipo de documento</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria jurídica</Label>
                <Select value={legalCategory ?? "none"} onValueChange={(v) => setLegalCategory(v === "none" ? null : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Opcional — ex.: Lei Federal, Convenção Coletiva" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma / Não classificado</SelectItem>
                    {LEGAL_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.group} — {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Pasta (organização)</Label>
                <Select value={folder ?? "none"} onValueChange={(v) => setFolder(v === "none" ? null : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {DOCUMENT_FOLDERS.filter((f) => f.value !== "outro").map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="outro">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Versão</Label>
                <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Conteúdo</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                placeholder="Cole ou digite o conteúdo do documento. A IA usará este texto para responder perguntas."
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingId ? "Salvar" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.title ?? "Documento"}</DialogTitle>
            <DialogDescription className="sr-only">Visualização do documento institucional selecionado</DialogDescription>
            {viewingDoc && (
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
                <Badge variant="secondary">{getDocumentTypeLabel(viewingDoc.document_type)}</Badge>
                {viewingDoc.folder && <Badge variant="outline">{getFolderLabel(viewingDoc.folder)}</Badge>}
                {viewingDoc.legal_category && (
                  <Badge variant="outline">{getLegalCategoryLabel(viewingDoc.legal_category)}</Badge>
                )}
                <span>v{viewingDoc.version}</span>
                {viewingDoc.attachment_url && (
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => openAttachment(viewingDoc)}>
                    <ExternalLink className="h-3 w-3 mr-1" /> Abrir arquivo
                  </Button>
                )}
              </div>
            )}
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0 rounded-lg border p-4">
            <DocumentPreview
              content={viewingDoc?.content || null}
              attachmentUrl={viewingDoc?.attachment_url || null}
              title={viewingDoc?.title}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentosInstituicao;
